/** @format */

import React, { lazy, Suspense } from 'react';
import { createRoot } from 'react-dom/client';
import Loader from '@components/loader';
import { TranslationProvider } from '@context/translation-context';

const ExerciseYourRightsForm = lazy(
  () =>
    import(
      /* webpackChunkName: "chris-toth-coffee-chat-form" */ './form-types/exercise-your-rights-form'
    )
);

const EdgeHyperarcEnAuForm = lazy(
  () =>
    import(
      /* webpackChunkName: "edge-hyperarc-en-au-form" */ './form-types/edge-hyperarc-en-au-form'
    )
);

const SubscribeForm = lazy(
  () =>
    import(
      /* webpackChunkName: "subscribe-form" */ './form-types/subscribe-form'
    )
);

const DemoForm = lazy(
  () =>
    import(
      /* webpackChunkName: "ai-segmentation-form" */ './form-types/ai-segmentation-demo-form'
    )
);

const WebinarForm = lazy(
  () =>
    import(/* webpackChunkName: "webinar-form" */ './form-types/webinar-form')
);

const InteroperabilityForm = lazy(
  () =>
    import(
      /* webpackChunkName: "interoperability-form" */ './form-types/interoperability-form'
    )
);

const ChrisTothCoffeeChatForm = lazy(
  () =>
    import(
      /* webpackChunkName: "chris-toth-coffee-chat-form" */ './form-types/chris-toth-coffee-chat-form'
    )
);

const CoffeeBreakForm = lazy(
  () =>
    import(
      /* webpackChunkName: "coffee-break-form" */ './form-types/coffee-break-form'
    )
);

const SupplierForm = lazy(
  () =>
    import(/* webpackChunkName: "supplier-form" */ './form-types/supplier-form')
);

const VarianInlineForm = lazy(
  () =>
    import(
      /* webpackChunkName: "dynamic-paragraph-form" */ './form-types/varian-form-paragraph/varian-inline-form'
    )
);

const SirForm = lazy(
  () => import(/* webpackChunkName: "sir-form" */ './form-types/sir-form')
);

const ContactFormNoAreaOfInquiry = lazy(
  () =>
    import(
      /* webpackChunkName: "contact-form-no-area-of-inquiry" */ './form-types/contact-form-no-area-of-inquiry'
    )
);

/**
 * Check to see if is form safe to load.  Used to avoid loading forms multiple
 * times.
 *
 * @returns
 */
const isFormSafeToLoad = (formElement: HTMLElement): boolean => {
  if (formElement && !formElement.classList.contains('loaded')) {
    formElement.classList.add('loaded');
    return true;
  }
  return false;
};

/**
 * Get Form Type.
 *
 * @param element
 * @returns
 */
const getFormType = (element: HTMLElement): JSX.Element | null => {
  if (element.id === 'subscribe-form-container') {
    return (
      <SubscribeForm
        marketoId={element.dataset.mid}
        formType={element.dataset.type}
      />
    );
  } else if (element.id === 'ai-demo-request-form') {
    return <DemoForm formContainer={element} disclaimer={element.innerHTML} />;
  } else if (element.id === 'third-party-integration-form') {
    return <InteroperabilityForm />;
  } else if (element.id === 'sir-form') {
    return <SirForm />;
  } else if (element.id === 'varian-supplier-form') {
    return <SupplierForm />;
  } else if (element.id === 'chris-toth-coffee-chat-form') {
    return <ChrisTothCoffeeChatForm />;
  } else if (element.id === 'virtual-coffee-break-form') {
    return <CoffeeBreakForm />;
  } else if (element.id === 'edge-hyperarc-en-au-form') {
    return <EdgeHyperarcEnAuForm />;
  } else if (element.classList.contains('contact-form-no-area-of-inquiry')) {
    return <ContactFormNoAreaOfInquiry />;
  } else if (element.id === 'exercise-your-rights-form') {
    return <ExerciseYourRightsForm />;
  } else if (element.classList.contains('redirect-form-button')) {
    return (
      <WebinarForm
        formId={element.dataset.program}
        linkText={element.dataset.button_text}
        formTitle={element.dataset.form_title}
        redirectUrl={element.dataset.redirect}
      />
    );
  } else if (element.classList.contains('varian-form-paragraph')) {
    const formData = JSON.parse(
      element.querySelector('.varian-form-data')?.innerText
    );
    if (formData) {
      return <VarainFormParagraph formData={formData} />;
    }
  } else if (element.classList.contains('varian-inline-form')) {
    const formData = {
      linkText: element.querySelector('.link-text')?.innerText,
      linkType: element.querySelector('.link-type')?.innerText,
      linkClasses: element.querySelector('.link-classes')?.innerText,
      formTitle: element.querySelector('.form-title')?.innerText,
      formFields: element.querySelector('.form-fields')?.innerText,
      formType: element.querySelector('.form-type')?.innerText,
      formId: element.querySelector('.form-id')?.innerText,
      formName: element.querySelector('.form-name')?.innerText,
      formDescription: element.querySelector('.form-description')?.innerText,
      emailTo: element.querySelector('.email-to')?.innerText,
      responseText: element.querySelector('.response-text')?.innerText,
      productId: element.querySelector('.product-id')?.innerText,
      formContainer: element.querySelector('.form-container')?.innerText,
      emailRedirect: element.querySelector('.email-redirect')?.innerText,
      responseMessage: element.querySelector('.response-message')?.innerText,
      emailTitle: element.querySelector('.email-title')?.innerText,
    };

    return <VarianInlineForm formData={formData} />;
  }
  return null;
};

/**
 * Forms.
 */
export const forms = (): void => {
  /**
   * Build Forms.
   */
  const buildForms = (): void => {
    const formContainers: NodeListOf<HTMLElement> = document.querySelectorAll(`
      #subscribe-form-container,
      #ai-demo-request-form,
      #third-party-integration-form,
      #sir-form,
      #virtual-coffee-break-form,
      .redirect-form-button,
      .varian-form-paragraph,
      #varian-supplier-form,
      .varian-inline-form,
      #chris-toth-coffee-chat-form,
      #contact-form-no-area-of-inquiry,
      #exercise-your-rights-form
    `);

    if (formContainers && formContainers.length > 0) {
      formContainers.forEach((form) => {
        if (isFormSafeToLoad(form)) {
          const root = createRoot(form);
          const formElement = (
            <TranslationProvider>
              <Suspense fallback={<Loader />}>{getFormType(form)}</Suspense>
            </TranslationProvider>
          );
          if (formElement) {
            root.render(formElement);
          }
        }
      });
    }
  };
  buildForms();
};
